import {
  DocumentData,
  DocumentReference,
  Transaction,
} from '@firebase/firestore-types';
import { Businesses, Users } from '@just-in-types';
import { firestore } from '../firebase';
import {
  cleanLanguage,
  cleanMobile,
  cleanName,
  cleanString,
  cleanVehicles,
} from './cleaningFunctions';

const businessesRef = firestore.collection('businesses');
const checkInsRef = firestore.collection('checkIns');
const usersRef = firestore.collection('users');

const getBusinessData = async (businessId: string) => {
  const doc = await businessesRef.doc(businessId).get();

  return {
    businessData: doc.exists
      ? ({ businessId, ...doc.data() } as Businesses.Business)
      : null,
  };
};

const getSiteData = async (businessId: string, siteId: string) => {
  const doc = await businessesRef
    .doc(businessId)
    .collection('sites')
    .doc(siteId)
    .get();

  return {
    siteData: doc.exists
      ? ({ siteId, ...doc.data() } as Businesses.Site)
      : null,
  };
};

const getUserData = async (userId: string) => {
  const doc = await usersRef.doc(userId).get();

  return {
    userData: doc.exists ? { userId, ...(doc.data() as Users.User) } : null,
  };
};

const getCheckInRefs = async () => {
  const query = await checkInsRef
    .where('businessId', '==', 'TuNj9xLoEpFEpus1Od68')
    .get();
  const checkInRefs: DocumentReference[] = [];

  console.warn('size', query.size);
  query.forEach(doc => checkInRefs.push(doc.ref));

  return checkInRefs;
};

const cleanCheckInData = async ({
  businessId,
  checkInData: {
    dateofbirth,
    mugshot,
    mugshotUrl,
    receiptCode,
    signature,
    signatureUrl,
    timestamp,
    vehicles,
    ...checkInData
  },
  checkInId,
  siteId,
  userId,
}: {
  businessId: string;
  checkInData: DocumentData;
  checkInId: string;
  siteId: string;
  userId: string;
}): Promise<null | Users.CheckIn> => {
  let businessPath: null | string | undefined = checkInData.businessPath;
  let email: null | string | undefined = checkInData.email;
  let language: null | string | undefined = checkInData.language;
  let mobile: null | string | undefined = checkInData.mobile;
  let name:
    | undefined
    | string
    | {
        displayName: null | string | undefined;
        firstname: null | string | undefined;
        lastname: null | string | undefined;
      } = checkInData.name;
  let sitePath: null | string | undefined = checkInData.sitePath;
  let zip: null | string | undefined = checkInData.zip;

  const { businessData } = await getBusinessData(businessId);

  if (businessData) {
    businessPath = businessData.path;
  }

  const { siteData } = await getSiteData(businessId, siteId);

  if (siteData) {
    sitePath = siteData.path;
  }

  const { userData } = await getUserData(userId);

  if (userData) {
    email = userData.email;
    language = userData.language;
    mobile = userData.mobile;
    name = userData.name;
    zip = userData.zip;
  }

  if (!businessId || !timestamp || !userId) {
    return null;
  } else {
    const mobileWithSecret = cleanMobile(mobile);

    return {
      businessId,
      businessPath: cleanString(businessPath),
      checkInId,
      dateofbirth,
      email: cleanString(email),
      language: cleanLanguage(language),
      mobile: mobileWithSecret.mobile,
      mugshot: cleanString(mugshot),
      mugshotUrl: cleanString(mugshotUrl),
      name: cleanName(name),
      receiptCode,
      secretMobile: mobileWithSecret.secretMobile,
      signature: cleanString(signature),
      signatureUrl: cleanString(signatureUrl),
      siteId,
      sitePath: cleanString(sitePath),
      timestamp,
      userId,
      vehicles: cleanVehicles(vehicles),
      zip: cleanString(zip),
    };
  }
};

const buildTransaction = async (checkInRef: DocumentReference) =>
  firestore.runTransaction(async transaction => {
    const checkInId = checkInRef.id;
    const checkInDoc = await transaction.get(checkInRef);
    const checkInData = checkInDoc.data();

    if (checkInData) {
      const { businessId, siteId, userId } = checkInData;

      const checkIn = await cleanCheckInData({
        businessId,
        checkInData,
        checkInId,
        siteId,
        userId,
      });

      if (checkIn) {
        return transaction.set(checkInRef, checkIn);
      } else {
        return transaction.delete(checkInRef);
      }
    } else {
      return transaction;
    }
  });

export default async () => {
  const checkInRefs = await getCheckInRefs();
  const transactions: Array<Promise<Transaction>> = [];

  for (const checkInRef of checkInRefs) {
    transactions.push(buildTransaction(checkInRef));
  }

  const results = await Promise.all(transactions);

  console.log(`${results.length} successfully cleaned!`);

  return results;
};



// WEBPACK FOOTER //
// ./src/adminFunctions/cleanCheckIns.ts